import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import {
  ThemeAITask,
  ThemeAITaskListResponse,
  ThemeAITaskStatus,
  ThemeAIDataSourceType,
  CreateThemeAITaskRequest,
  CarbonInterviewRawDataRow,
  ThemeAIUploadInspection,
} from '../types/gotypes'

export interface ThemeAITaskSampleResponse {
  pool_size: number
  sample_size: number
  variable_name?: string
  rows: CarbonInterviewRawDataRow[]
  /** The organisation and job context from Admin → Context. */
  job_context?: string
}

export interface ThemeAITaskFilters {
  status?: ThemeAITaskStatus
  starred?: boolean
  dataSource?: ThemeAIDataSourceType
  search?: string
  includeArchived?: boolean
}

interface UseThemeAITasksOptions {
  customer: string
  job: string
  filters?: ThemeAITaskFilters
  taskId?: string
  /** When set, keep a preview of this many of the task's responses (the Data stage). */
  previewSize?: number
}

// All networking for the ThemeAI Task list/detail lives here (Page -> Hook ->
// Component). Design spec #916 §3; API contract from PR #948
// (goapi/pkg/server/themeai_tasks.go).
export const useThemeAITasks = ({ customer, job, filters, taskId, previewSize }: UseThemeAITasksOptions) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()

  const baseUrl = `${API_BASE_URL}/themeai/${customer}/${job}`
  const listQueryKey = ['themeai_tasks', { customer, job, filters }]
  const taskQueryKey = ['themeai_task', { customer, job, taskId }]

  const tasksQuery = useQuery({
    queryKey: listQueryKey,
    enabled: Boolean(customer && job && account.user),
    queryFn: async () => {
      const res = await axios.get<ThemeAITaskListResponse>(`${baseUrl}/tasks`, {
        params: {
          status: filters?.status || undefined,
          starred: filters?.starred ? 'true' : undefined,
          data_source: filters?.dataSource || undefined,
          search: filters?.search || undefined,
          include_archived: filters?.includeArchived ? 'true' : undefined,
        },
      })
      return res.data
    },
  })

  const taskQuery = useQuery({
    queryKey: taskQueryKey,
    enabled: Boolean(customer && job && taskId && account.user),
    queryFn: async () => {
      const res = await axios.get<ThemeAITask>(`${baseUrl}/tasks/${taskId}`)
      return res.data
    },
  })

  // Returns the invalidation promise so every mutation below can `return` it
  // from onSuccess — TanStack v5 awaits whatever onSuccess returns before
  // flipping isPending back to false, so an awaited mutateAsync() sees
  // post-invalidation state instead of a flash of stale cached data (this is
  // what caused the "stage not available yet" flash after saves).
  const invalidateAll = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['themeai_tasks', { customer, job }], exact: false }),
    ...(taskId ? [queryClient.invalidateQueries({ queryKey: taskQueryKey })] : []),
  ])

  const createTaskMutation = useMutation({
    mutationFn: async (payload: CreateThemeAITaskRequest) => {
      const res = await axios.post<ThemeAITask>(`${baseUrl}/tasks`, payload)
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const updateTaskMutation = useMutation({
    mutationFn: async (payload: { id: string } & Partial<CreateThemeAITaskRequest>) => {
      const { id, ...data } = payload
      const res = await axios.put<ThemeAITask>(`${baseUrl}/tasks/${id}`, data)
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const archiveTaskMutation = useMutation({
    mutationFn: async (payload: { id: string; archived: boolean }) => {
      const res = await axios.post<{ id: string; status: ThemeAITaskStatus }>(
        `${baseUrl}/tasks/${payload.id}/archive`,
        { archived: payload.archived },
      )
      return res.data
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => {
      snackbar.error(extractErrorMessage(error))
    },
  })

  const starTaskMutation = useMutation({
    mutationFn: async (payload: { id: string; starred: boolean }) => {
      const res = await axios.post<{ id: string; starred: boolean }>(
        `${baseUrl}/tasks/${payload.id}/star`,
        { starred: payload.starred },
      )
      return res.data
    },
    // Optimistic — starring should feel instant.
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey })
      const previous = queryClient.getQueryData<ThemeAITaskListResponse>(listQueryKey)
      if (previous) {
        const toggle = (tasks: ThemeAITask[]) =>
          tasks.map((t) => (t.id === payload.id ? { ...t, starred: payload.starred } : t))
        queryClient.setQueryData<ThemeAITaskListResponse>(listQueryKey, {
          exec: toggle(previous.exec),
          mine: toggle(previous.mine),
          colleagues: toggle(previous.colleagues),
        })
      }
      return { previous }
    },
    onError: (error, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(listQueryKey, context.previous)
      }
      snackbar.error(extractErrorMessage(error))
    },
    onSettled: () => invalidateAll(),
  })

  const createTask = (payload: CreateThemeAITaskRequest) => createTaskMutation.mutateAsync(payload)
  const updateTask = (id: string, payload: Partial<CreateThemeAITaskRequest>) =>
    updateTaskMutation.mutateAsync({ id, ...payload })
  const archiveTask = (id: string, archived: boolean = true) =>
    archiveTaskMutation.mutateAsync({ id, archived })
  const starTask = (id: string, starred: boolean) => starTaskMutation.mutateAsync({ id, starred })
  // A Manager stars a task to or from Company-level, as with the code frame
  // library (`useThemeAICodeFrames.handlePromoteCodeFrame`) and Data Viz
  // (#1523 item 2). This is a visibility-only PUT — the server's
  // canPromoteThemeAITask allows a Manager to do it on a colleague's task too.
  const promoteTask = (id: string) => updateTask(id, { visibility: 'exec' })
  const demoteTask = (id: string) => updateTask(id, { visibility: 'user' })

  // Upload bytes first, then let the server atomically replace the task's
  // source metadata and current record-pool membership. text_column /
  // id_column are optional overrides for the picker in the upload/inspect
  // flow — omitted, the server falls back to its own heuristic.
  const uploadTaskDataMutation = useMutation({
    mutationFn: async (payload: { id: string; file: File; textColumn?: string; idColumn?: string }) => {
      const form = new FormData()
      form.append('file', payload.file)
      if (payload.textColumn) form.append('text_column', payload.textColumn)
      if (payload.idColumn) form.append('id_column', payload.idColumn)
      const res = await axios.post<{ task: ThemeAITask }>(`${baseUrl}/tasks/${payload.id}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data.task
    },
    onSuccess: () => invalidateAll(),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })
  const uploadTaskData = (
    id: string,
    file: File,
    columns?: { textColumn?: string; idColumn?: string },
  ) => uploadTaskDataMutation.mutateAsync({ id, file, textColumn: columns?.textColumn, idColumn: columns?.idColumn })

  // POST /uploads/inspect — task-independent column preview shown before the
  // upload/re-upload so the user can pick text_column/id_column up front.
  const inspectUploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const res = await axios.post<ThemeAIUploadInspection>(`${baseUrl}/uploads/inspect`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
  })
  // The upload step shows this error in place, so it rejects with the
  // server's message rather than snackbarring it.
  const inspectUpload = (file: File) =>
    inspectUploadMutation.mutateAsync(file).catch((error: unknown) => {
      throw new Error(extractErrorMessage(error))
    })

  // Re-runs the task's own data source. A job-variable task created before
  // creation imported its responses has an empty pool; this fills it.
  const fetchTaskDataMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post(`${baseUrl}/tasks/${taskId}/data/attach`)
      return res.data
    },
    onSuccess: () => Promise.all([
      invalidateAll(),
      queryClient.invalidateQueries({ queryKey: ['themeai_task_preview', { customer, job, taskId }], exact: false }),
    ]),
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })
  const fetchTaskData = () => fetchTaskDataMutation.mutateAsync().then(() => undefined)

  const taskSampleMutation = useMutation({
    mutationFn: async (sampleSize: number) => {
      const res = await axios.get<ThemeAITaskSampleResponse>(`${baseUrl}/tasks/${taskId}/records/sample`, {
        params: { n: sampleSize },
      })
      return res.data
    },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })
  const loadTaskSample = (sampleSize: number = 200) => taskSampleMutation.mutateAsync(sampleSize)

  // A preview of the task's responses. Keyed on the data revision so a
  // replaced source shows its own responses, never the old ones.
  const previewQuery = useQuery({
    queryKey: ['themeai_task_preview', { customer, job, taskId, revision: taskQuery.data?.data_revision, previewSize }],
    enabled: Boolean(customer && job && taskId && previewSize && taskQuery.data && account.user),
    queryFn: async () => {
      const res = await axios.get<ThemeAITaskSampleResponse>(`${baseUrl}/tasks/${taskId}/records/sample`, {
        params: { n: previewSize },
      })
      return res.data
    },
  })

  return {
    tasks: tasksQuery.data || { exec: [], mine: [], colleagues: [] },
    tasksLoading: tasksQuery.isLoading,
    tasksError: tasksQuery.error,

    task: taskQuery.data,
    taskLoading: taskQuery.isLoading,
    taskError: taskQuery.error,

    createTask,
    updateTask,
    archiveTask,
    starTask,
    promoteTask,
    demoteTask,
    uploadTaskData,
    inspectUpload,
    loadTaskSample,
    fetchTaskData,
    preview: previewQuery.data,
    previewLoading: previewQuery.isLoading,

    createTaskLoading: createTaskMutation.isPending,
    updateTaskLoading: updateTaskMutation.isPending,
    uploadTaskDataLoading: uploadTaskDataMutation.isPending,
    inspectUploadLoading: inspectUploadMutation.isPending,
    taskSampleLoading: taskSampleMutation.isPending,
    fetchTaskDataLoading: fetchTaskDataMutation.isPending,

    refetchTasks: tasksQuery.refetch,
    refetchTask: taskQuery.refetch,
  }
}

export default useThemeAITasks
